import { parseContentUri } from "lib/actions/assets/parseContentUri";
import { AzureStorageResource } from "../AzureStorageResource";
import { AssetTypeAdapterInterface } from "./AssetTypeAdapterInterface";
import { RenderableAsset } from "lib/types/assets/RenderableAsset";
import { Resource } from "cesium";
import { Guard } from "lib/actions/util";
import CameraPlugin from "components/elements/CesiumViewer/core/wrapper/CameraPlugin";
import ScansetPlugin from "../ScansetPlugin";

export class ScansetAssetTypeAdapter implements AssetTypeAdapterInterface {
  constructor(
    private readonly _scanset: ScansetPlugin,
    private readonly _camera: CameraPlugin,
  ) {}

  isExisting(uri: string) {
    return this._scanset.isExisting(uri);
  }

  isReady(uri: string) {
    return this._scanset.isReady(uri);
  }

  isShowing(uri: string) {
    return this._scanset.isShowing(uri);
  }

  async load(version: RenderableAsset) {
    Guard.require(version, "version");
    const contentUri = version.contentUri;
    const resource = await this.createResource(
      contentUri,
      version.asset.projectId,
    );
    await this._scanset.load(contentUri, resource);
  }

  show(uri: string) {
    this._scanset.show(uri);
  }

  hide(uri: string) {
    this._scanset.hide(uri);
  }

  async flyTo(uri: string): Promise<void> {
    Guard.require(uri, "uri");
    const scanset = this._scanset.get(uri);
    if (scanset) {
      this._camera.flyToBounds([
        scanset.bbox[0] - 0.00025,
        scanset.bbox[1] - 0.00025,
        scanset.bbox[2] + 0.00025,
        scanset.bbox[3] + 0.00025,
      ]);
    }
  }

  private async createResource(
    contentUri: string,
    projectId: number,
  ): Promise<Resource> {
    const uri = parseContentUri(contentUri);
    if (uri.type !== "tileset") {
      throw new Error(
        `Invalid content URI type. Type '${uri.type}' must be a 'tileset'.`,
      );
    }

    let resource: Resource = null;
    if (uri.location === "self") {
      if (!uri.value.endsWith(".json")) {
        throw new Error(
          `Invalid content URI value. URL '${uri.value}' must be a .json tileset.`,
        );
      }
      resource = await AzureStorageResource.fromUrl(uri.value, projectId);
    } else {
      throw new Error(
        `Invalid content URI location. Location '${uri.location}' not supported.`,
      );
    }
    return resource;
  }
}
